import React, { useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  Chip,
  Alert,
} from "@mui/material";
import AppModal from "../common/AppModal";
import AppButton from "../buttons/AppButton";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { receivePurchaseOrderItems } from "../../redux/actions/purchaseOrderActions";
import { PurchaseOrderResponseDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderResponseDto";
import { PurchaseOrderReceiveRequestDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderReceiveRequestDto";
import { PurchaseOrderItemStatus } from "../../shared/enums/PurchaseOrderItemStatus";
import FormAlert from "../common/FormAlert";

interface ReceiveItemField {
  itemId: string;
  name: string;
  ordered: number;
  receivableQty: number;
  receivedQuantity: number;
  unitCost: number;
  status: PurchaseOrderItemStatus;
  checked: boolean;
}

const ReceivePoModal: React.FC<{
  open: boolean;
  onClose: () => void;
  order: PurchaseOrderResponseDto;
}> = ({ open, onClose, order }) => {
  const dispatch = useAppDispatch();
  const { loadingReceive, errorReceive } = useAppSelector(
    (state) => state.purchaseOrder,
  );

  const [items, setItems] = useState<ReceiveItemField[]>(
    (order.items || [])
      .filter(
        (i) =>
          i.status === PurchaseOrderItemStatus.EXPECTED ||
          i.status === PurchaseOrderItemStatus.CANCELLED,
      )
      .map((i) => {
        const ordered = Number(i.orderedQuantity || 0);
        const received = Number(i.receivedQuantity || 0);
        const receivable = Math.max(0, ordered - received);
        return {
          itemId: i.id,
          name: i.productName || "Item",
          ordered,
          receivableQty: receivable,
          receivedQuantity: receivable,
          unitCost: Number(i.unitCost || 0),
          status: PurchaseOrderItemStatus.RECEIVED,
          checked: false,
        };
      }),
  );

  const handleReceive = (): void => {
    const payload: PurchaseOrderReceiveRequestDto = {
      items: items
        .filter((i) => i.checked)
        .map((i) => ({
          itemId: i.itemId,
          receivedQuantity: Number(i.receivedQuantity),
          unitCost: Number(i.unitCost),
          status: i.status,
        })),
      chargeIds: null,
    };

    dispatch(receivePurchaseOrderItems(order.id, payload));
  };

  return (
    <AppModal
      open={open}
      onClose={loadingReceive ? () => {} : onClose}
      title="Receive Goods"
      width="lg"
    >
      <FormAlert message={errorReceive} severity="error" />

      <Stack spacing={3}>
        <Alert severity="info">
          Select items and confirm quantities and unit landing costs for this
          delivery batch.
        </Alert>

        <Box
          sx={{
            maxHeight: 400,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ bgcolor: "background.default" }}
                />
                <TableCell sx={{ bgcolor: "background.default" }}>
                  Product
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ bgcolor: "background.default" }}
                >
                  Receivable
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ bgcolor: "background.default" }}
                >
                  Qty Received
                </TableCell>
                <TableCell align="right" sx={{ bgcolor: "background.default" }}>
                  Actual Unit Cost
                </TableCell>
                <TableCell sx={{ bgcolor: "background.default" }}>
                  Condition
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={item.itemId}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={item.checked}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].checked = e.target.checked;
                        setItems(arr);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {item.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={item.receivableQty} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      disabled={!item.checked}
                      value={item.receivedQuantity}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].receivedQuantity = Number(e.target.value);
                        setItems(arr);
                      }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      disabled={!item.checked}
                      value={item.unitCost}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].unitCost = Number(e.target.value);
                        setItems(arr);
                      }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      disabled={!item.checked}
                      value={item.status}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].status = e.target
                          .value as PurchaseOrderItemStatus;
                        setItems(arr);
                      }}
                    >
                      <MenuItem value={PurchaseOrderItemStatus.RECEIVED}>
                        Good Condition
                      </MenuItem>
                      <MenuItem value={PurchaseOrderItemStatus.DAMAGED}>
                        Damaged
                      </MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <AppButton
          variant="contained"
          color="primary"
          onClick={handleReceive}
          disabled={!items.some((i) => i.checked) || loadingReceive}
        >
          {loadingReceive
            ? "Processing..."
            : "Confirm Reception & Update Stock"}
        </AppButton>
      </Stack>
    </AppModal>
  );
};

export default ReceivePoModal;
